import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  useGetAllReports,
  useGetReportStatistics,
  useGetPendingReportCount,
  type GetAllReportsParams,
} from '@edusense/report-client';
import {
  REPORT_STATUS_COLOR_MAP,
  REPORT_STATUS_LABELS,
  REPORT_PRIORITY_COLOR_MAP,
  REPORT_PRIORITY_LABELS,
  REPORT_TARGET_TYPE_LABELS,
  REPORT_REASON_LABELS,
  formatKoreanDate,
} from '../types/report';

const DEFAULT_RECENT_REPORTS_COUNT = 6;

export default function DashboardPage() {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 7);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, []);

  const statistics = useGetReportStatistics({ startDate, endDate });
  const pendingCount = useGetPendingReportCount();

  const listParams: GetAllReportsParams = {
    limit: DEFAULT_RECENT_REPORTS_COUNT,
    offset: 0,
  };

  const recentReportsQuery = useGetAllReports(listParams, {
    query: {
      keepPreviousData: true,
    },
  });

  const statusDistribution = statistics.data?.statusDistribution ?? {};

  const overviewCards = [
    {
      label: '총 신고 건수 (7일)',
      value: statistics.data?.totalCount ?? 0,
      colorClass: 'text-primary',
      isLoading: statistics.isLoading,
      unavailable: statistics.isError || !statistics.data,
    },
    {
      label: '대기 중 (전체)',
      value: pendingCount.data?.count ?? 0,
      colorClass: 'text-yellow-600',
      isLoading: pendingCount.isLoading,
      unavailable: pendingCount.isError || !pendingCount.data,
    },
    {
      label: '검토 중 (7일)',
      value: statusDistribution.REVIEWING ?? 0,
      colorClass: 'text-violet-700',
      isLoading: statistics.isLoading,
      unavailable: statistics.isError || !statistics.data,
    },
    {
      label: '처리 완료 (7일)',
      value: statusDistribution.RESOLVED ?? 0,
      colorClass: 'text-green-600',
      isLoading: statistics.isLoading,
      unavailable: statistics.isError || !statistics.data,
    },
  ];

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-200/80 pb-6 dark:border-slate-800">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">신고 현황</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">최근 7일의 신고 통계와 최신 접수 내역을 확인하세요.</p>
        </div>
        <p className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          신고 통계: 최근 7일 · 대기 건수: 전체 기간
        </p>
      </div>

      {statistics.isError && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <span>신고 통계를 불러오지 못했습니다.</span>
          <Button variant="outline" size="sm" disabled={statistics.isFetching} onClick={() => void statistics.refetch()}>신고 통계 다시 시도</Button>
        </div>
      )}
      {pendingCount.isError && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <span>전체 대기 건수를 불러오지 못했습니다.</span>
          <Button variant="outline" size="sm" disabled={pendingCount.isFetching} onClick={() => void pendingCount.refetch()}>대기 건수 다시 시도</Button>
        </div>
      )}

      {/* Statistics Cards Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {overviewCards.map((card, idx) => {
          const icons = ['📊', '⏳', '🔍', '✅'];
          const borders = [
            'hover:border-blue-500/40 hover:shadow-blue-500/5',
            'hover:border-amber-500/40 hover:shadow-amber-500/5',
            'hover:border-purple-500/40 hover:shadow-purple-500/5',
            'hover:border-emerald-500/40 hover:shadow-emerald-500/5'
          ];
          const bgGradients = [
            'from-blue-500/5 to-transparent',
            'from-amber-500/5 to-transparent',
            'from-purple-500/5 to-transparent',
            'from-emerald-500/5 to-transparent'
          ];

          return (
            <Card key={card.label} className={`relative overflow-hidden border border-slate-200/80 bg-white transition-all duration-200 shadow-sm hover:shadow-md ${borders[idx]} dark:border-slate-800 dark:bg-slate-900`}>
              <div className={`absolute inset-0 bg-gradient-to-b ${bgGradients[idx]} pointer-events-none`}></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {card.label}
                  </span>
                  <span className="text-lg">{icons[idx]}</span>
                </div>
                {card.isLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="text-xs text-slate-400">불러오는 중...</span>
                  </div>
                ) : card.unavailable ? (
                  <p className="py-2 text-sm font-semibold text-destructive">확인 불가</p>
                ) : (
                  <p className={`text-3xl font-extrabold tracking-tight ${card.colorClass}`}>
                    {card.value.toLocaleString()}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Reports Table */}
      <Card className="border border-slate-200/80 shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center bg-slate-50/50 border-b border-slate-200/80 px-6 py-4 dark:bg-slate-950/30 dark:border-slate-800">
          <div>
            <CardTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
              최근 신고
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">최근 접수된 신고와 현재 처리 상태</p>
          </div>
          <Link 
            to="/reports" 
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 px-3 py-1.5 rounded-lg border border-blue-200/60 dark:border-blue-900/40 transition-colors"
          >
            전체 신고 내역 보기 →
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <Table aria-label="최근 신고 목록" className="min-w-[680px]">
            <TableHeader className="bg-slate-50/40 dark:bg-slate-950/20">
              <TableRow className="border-b border-slate-200/80 dark:border-slate-800">
                <TableHead className="font-semibold text-xs py-3.5 pl-6">ID</TableHead>
                <TableHead className="font-semibold text-xs py-3.5">신고 대상</TableHead>
                <TableHead className="font-semibold text-xs py-3.5">접수 사유</TableHead>
                <TableHead className="font-semibold text-xs py-3.5">처리 상태</TableHead>
                <TableHead className="font-semibold text-xs py-3.5">우선순위</TableHead>
                <TableHead className="font-semibold text-xs py-3.5 pr-6">접수 일시</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentReportsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" /> 
                      <span className="text-xs font-medium">신고 목록을 불러오는 중입니다...</span>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {recentReportsQuery.isError && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">
                    <div role="alert" className="space-y-3 text-sm text-destructive">
                      <p>최근 신고 목록을 불러오지 못했습니다.</p>
                      <Button variant="outline" size="sm" disabled={recentReportsQuery.isFetching} onClick={() => void recentReportsQuery.refetch()}>신고 목록 다시 시도</Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {recentReportsQuery.isSuccess && recentReportsQuery.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">최근 신고가 없습니다.</TableCell>
                </TableRow>
              )}

              {recentReportsQuery.isSuccess && recentReportsQuery.data.map((report) => (
                <TableRow key={report.id} className="border-b border-slate-100 hover:bg-slate-50/60 dark:border-slate-800/60 dark:hover:bg-slate-800/30 transition-colors">
                  <TableCell className="pl-6 py-3.5 font-mono text-xs font-semibold">
                    <Link to={`/reports/${report.id}`} className="text-blue-600 hover:underline dark:text-blue-400">
                      #{report.id}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs font-medium text-slate-800 dark:text-slate-200">
                    {REPORT_TARGET_TYPE_LABELS[report.targetType]}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 dark:text-slate-400">
                    {REPORT_REASON_LABELS[report.reason]}
                  </TableCell>
                  <TableCell>
                    <Badge variant={REPORT_STATUS_COLOR_MAP[report.status]} className="text-[11px] font-semibold">
                      {REPORT_STATUS_LABELS[report.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={REPORT_PRIORITY_COLOR_MAP[report.priority]} className="text-[11px] font-semibold">
                      {REPORT_PRIORITY_LABELS[report.priority]}
                    </Badge>
                  </TableCell>
                  <TableCell className="pr-6 text-xs text-slate-500">
                    {formatKoreanDate(report.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
